<script setup lang="ts">
import type { AgentOnPage } from '@@/shared/agent/api-types';
import type { IadAppRoute } from '~/application';

const props = defineProps<{
  agent: AgentOnPage
}>();

// SHARE
const { t } = useI18n();

const title = computed(() => {
  const { place, postcode } = props.agent.location;
  const translationKey = props.agent.displayGender === `female`
    ? `agent.header.agent_in_female`
    : `agent.header.agent_in_male`;
  return t(translationKey, { place, postcode });
});

const displayContactForm = defineModel<boolean>();

function onContact() {
  displayContactForm.value = true;
  trackEvent({
    event: 'agent_interaction',
    event_name: 'click_to_email',
    agent_id: props.agent.localId,
  });
  trackEvent({
    event: 'generate_lead',
    lead_category: 'estimate',
    agent_id: props.agent.localId,
  });
}

// AGENT PAGE LINK
const isMounted = useMounted();
const agentPageLink = computed(() => {
  if (!isMounted.value) return {};
  const agentPageLink: IadAppRoute<`real-estate-agent-userName`> = {
    name: `real-estate-agent-userName`,
    params: { userName: props.agent.userName },
  };
  return {
    to: agentPageLink,
  };
});
</script>

<template>
  <div class="content">
    <IadWrapper
      class="bg-secondary text-white pb-[136px]"
      inner-classes="lg:p-l"
      :padded="false"
    >
      <div class="flex flex-col md:flex-row items-center gap-l">
        <AgentAvatarLarge
          :agent
          variant="agent-page"
        />
        <div class="flex-1 px-m flex flex-col gap-m">
          <div class="flex justify-between">
            <IadTitle
              variant="large"
              tag="h1"
              :text="agent.fullName"
            />
            <AgentValuationShare
              :title
              :agent
            />
          </div>
          <IadTitle
            tag="h2"
            variant="small-semibold"
            class="pt-2xs"
            :text="title"
          />
          <IadReviewSummary
            v-if="agent.reviewsCount"
            :average-rating="agent.reviewsRatingAverage"
            :reviews-count="agent.reviewsCount"
          />
          <AgentLanguages
            class="hidden lg:flex"
            :agent
          />
          <div class="hidden lg:flex flex-wrap gap-m">
            <IadBtn
              size="large"
              @click="onContact"
            >
              <Icon name="iad-icon:mail" />
              {{ $t('core.common.send_mail') }}
            </IadBtn>
            <AgentBtnPhone
              size="large"
              :agent
            />
            <IadBtn
              size="large"
              class="w-full md:w-auto"
              v-bind="agentPageLink"
              :text="$t('agent.estimate.header.see_portal')"
            />
          </div>
        </div>
      </div>
    </IadWrapper>
    <ClientOnly>
      <AgentDialogContact
        v-model="displayContactForm"
        :agent
      />
    </ClientOnly>
  </div>
</template>
